import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * الإملاءُ من المتصفّح (Web Speech API).
 *
 * <b>ولا يُحلَّل هنا حرفٌ واحد.</b> ما يخرج من الجهاز يُوضع في الحقل كما هو
 * ويُرسل إلى الخادم، والخادمُ يحلّله بجداولٍ مقيسةٍ باختبارات. ولو حلّلت
 * الواجهةُ لَصار لنا محلّلان يتفرّقان بلا أن يلاحظ أحد، ولَما نفع قياسُ الدقّة
 * الذي بُني على الخادم.
 *
 * <b>والدعمُ ليس عامًّا</b>: كروم وإدج وسفاري تدعمه، وفَيَرفُكس لا. ولهذا
 * `supported` تُصدَّر ويُبنى عليها: شاشةٌ تفترض الميكروفونَ موجودًا تسقط في
 * وجه موظّفٍ لا يملك إلا كتابةً — وشاشتُنا تُبقي حقلَ الكتابة ظاهرًا دائمًا.
 */

type SpeechResultAlternative = { transcript: string; confidence: number }
type SpeechResult = { 0: SpeechResultAlternative; isFinal: boolean; length: number }

type SpeechEvent = {
  resultIndex: number
  results: { length: number; [index: number]: SpeechResult }
}

type Recognizer = {
  lang: string
  continuous: boolean
  interimResults: boolean
  maxAlternatives: number
  start: () => void
  stop: () => void
  abort: () => void
  onresult: ((event: SpeechEvent) => void) | null
  onerror: ((event: { error: string }) => void) | null
  onend: (() => void) | null
}

type RecognizerConstructor = new () => Recognizer

function recognizerClass(): RecognizerConstructor | null {
  const w = window as unknown as {
    SpeechRecognition?: RecognizerConstructor
    webkitSpeechRecognition?: RecognizerConstructor
  }

  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

/** رسائلُ الأخطاء المعروفة — والمجهولُ يُعرض برمزه لا بصمت. */
const errorNames: Record<string, string> = {
  'not-allowed': 'المتصفّحُ منع الميكروفون. اسمح به من إعدادات الموقع.',
  'service-not-allowed': 'خدمةُ التعرّف ممنوعة في هذا المتصفّح.',
  'no-speech': 'لم يُسمع شيء. اقترب من الميكروفون وأعد المحاولة.',
  'audio-capture': 'لا ميكروفونَ متاح.',
  network: 'التعرّفُ على الكلام يحتاج اتّصالًا بالإنترنت.',
  aborted: '',
}

export type Dictation = {
  supported: boolean
  listening: boolean
  /** ما سُمع ولم يُثبَّت بعد — يُعرض شاحبًا ليرى الموظّفُ أنّ الجهاز يسمعه. */
  interim: string
  error: string | null
  start: () => void
  stop: () => void
}

/**
 * @param onText يُنادى بكلّ مقطعٍ <b>نهائيّ</b> — والمؤقّتُ لا يُرسل: نصفُ جملةٍ
 * يصل الخادمَ فيخرج منه اسمُ صنفٍ ناقص.
 */
export function useDictation(onText: (text: string) => void): Dictation {
  const [listening, setListening] = useState(false)
  const [interim, setInterim] = useState('')
  const [error, setError] = useState<string | null>(null)
  const recognizer = useRef<Recognizer | null>(null)

  // المُنادى في ref لا في التبعيّات: لو دخل التبعيّاتِ لَأُعيد بناءُ المُتعرِّف
  // مع كلّ رسمة، فينقطع الاستماعُ في منتصف الجملة.
  // ويُحدَّث في أثر لا في الرسم: الكتابةُ في ref أثناء الرسم تكسر ما يبنيه
  // React مرّتين في الوضع الصارم.
  const sink = useRef(onText)

  useEffect(() => {
    sink.current = onText
  }, [onText])

  const supported = recognizerClass() !== null

  useEffect(() => () => recognizer.current?.abort(), [])

  const start = useCallback(() => {
    const Recognition = recognizerClass()

    if (!Recognition) {
      setError('هذا المتصفّحُ لا يدعم الإملاء. اكتب النصَّ في الحقل.')
      return
    }

    recognizer.current?.abort()

    const recognition = new Recognition()

    // ‏ar-JO لا ar-SA: اللهجةُ المقيسة في جداول الأعداد أردنيّةٌ («حداعش»،
    // «ليرتين»)، ومطابقةُ لغة التعرّف لها تُقلّل ما يصل الخادمَ مشوّشًا.
    recognition.lang = 'ar-JO'
    recognition.continuous = true
    recognition.interimResults = true
    recognition.maxAlternatives = 1

    recognition.onresult = (event) => {
      let pending = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]

        if (result.isFinal) {
          const text = result[0].transcript.trim()

          if (text) {
            sink.current(text)
          }
        } else {
          pending += result[0].transcript
        }
      }

      setInterim(pending)
    }

    recognition.onerror = (event) => {
      const message = errorNames[event.error] ?? `تعذّر الإملاء (${event.error}).`

      if (message) {
        setError(message)
      }
    }

    recognition.onend = () => {
      setListening(false)
      setInterim('')
    }

    recognizer.current = recognition
    setError(null)
    setInterim('')
    setListening(true)
    recognition.start()
  }, [])

  const stop = useCallback(() => {
    recognizer.current?.stop()
    setListening(false)
  }, [])

  return { supported, listening, interim, error, start, stop }
}
